import Markdoc, { Config, nodes as defaultNodes } from "@markdoc/markdoc";

const Tag = Markdoc.Tag;

/* ── Custom Nodes ──────────────────────────────────────────────── */

// Nodes WITH built-in transforms need custom transforms to use our render names
const heading = {
  ...defaultNodes.heading,
  transform(node: any, config: any) {
    const attrs = node.transformAttributes(config);
    const children = node.transformChildren(config);
    return new Tag("Heading", { ...attrs, level: node.attributes.level }, children);
  },
};

const paragraph = { ...defaultNodes.paragraph, render: "Paragraph" };

const fence = {
  ...defaultNodes.fence,
  transform(node: any, config: any) {
    const attrs = node.transformAttributes(config);
    return new Tag("CodeBlock", { ...attrs, language: node.attributes.language, content: node.attributes.content }, []);
  },
};

const link = { ...defaultNodes.link, render: "MarkdocLink" };
const image = { ...defaultNodes.image, render: "MarkdocImage" };
const blockquote = { ...defaultNodes.blockquote, render: "Blockquote" };

const list = {
  ...defaultNodes.list,
  transform(node: any, config: any) {
    const children = node.transformChildren(config);
    return new Tag("List", { ordered: node.attributes.ordered }, children);
  },
};

const item = { ...defaultNodes.item, render: "ListItem" };
const hr = { ...defaultNodes.hr, render: "HorizontalRule" };

const code = {
  ...defaultNodes.code,
  transform(node: any, config: any) {
    const attrs = node.transformAttributes(config);
    const children = node.transformChildren(config);
    return new Tag("InlineCode", attrs, children);
  },
};

const strong = { ...defaultNodes.strong, render: "Strong" };
const em = { ...defaultNodes.em, render: "Emphasis" };
const s = { ...defaultNodes.s, render: "Strikethrough" };

const table = { ...defaultNodes.table, render: "Table" };
const thead = { ...defaultNodes.thead, render: "TableHead" };
const tbody = { ...defaultNodes.tbody, render: "TableBody" };
const tr = { ...defaultNodes.tr, render: "TableRow" };
const th = { ...defaultNodes.th, render: "TableHeader" };
const td = { ...defaultNodes.td, render: "TableCell" };

/* ── Custom Tags ({% tag %} syntax) ──────────────────────────────── */

const callout = {
  render: "Callout",
  description: "A styled callout box",
  children: ["paragraph", "tag", "list"],
  attributes: {
    type: { type: String, default: "info", matches: ["info", "warning", "error", "success"] },
    title: { type: String },
  },
};

const tabs = { render: "Tabs", children: ["tag"], attributes: {} };

const tab = {
  render: "Tab",
  children: ["paragraph", "tag", "list", "fence", "heading"],
  attributes: { label: { type: String, required: true } },
};

const badge = {
  render: "MarkdocBadge",
  selfClosing: true,
  attributes: {
    text: { type: String, required: true },
    variant: { type: String, default: "default", matches: ["default", "secondary", "destructive", "outline"] },
  },
};

const step = {
  render: "Step",
  children: ["paragraph", "tag", "list"],
  attributes: {
    num: { type: String, required: true },
    title: { type: String, required: true },
  },
};

const card = {
  render: "Card",
  children: ["paragraph", "tag", "list"],
  attributes: { title: { type: String, required: true } },
};

const endpoint = {
  render: "Endpoint",
  selfClosing: true,
  attributes: {
    method: { type: String, required: true },
    path: { type: String, required: true },
    description: { type: String, required: true },
  },
};

const sectionHeading = {
  render: "SectionHeading",
  selfClosing: true,
  attributes: {
    id: { type: String, required: true },
    title: { type: String, required: true },
    icon: { type: String, required: true },
  },
};

const scoreRange = {
  render: "ScoreRange",
  selfClosing: true,
  attributes: {
    range: { type: String, required: true },
    label: { type: String, required: true },
    color: { type: String, required: true },
    description: { type: String, required: true },
  },
};

/* ── Export Config ──────────────────────────────────────────────── */

export const markdocConfig: Config = {
  nodes: {
    heading, paragraph, fence, link, image, blockquote,
    list, item, hr, code, strong, em, s,
    table, thead, tbody, tr, th, td,
  },
  tags: {
    callout, tabs, tab, badge, step, card, endpoint,
    "section-heading": sectionHeading,
    "score-range": scoreRange,
  },
};
